"use client";

import Image from "next/image";
import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/ui/Reveal";
import { AWARDS } from "@/lib/site-data";

const AWARD_ICONS = [
  // 1. Trophy / Creator Award (ICCA 2026 Mumbai)
  <svg key="trophy" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 9V4a1 1 0 011-1h10a1 1 0 011 1v5a6 6 0 01-5 5.917V18h3a1 1 0 110 2H8a1 1 0 110-2h3v-3.083A6 6 0 016 9z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 6H4a2 2 0 00-2 2v1a4 4 0 004 4h1m11-7h2a2 2 0 012 2v1a4 4 0 01-4 4h-1" />
  </svg>,
  // 2. Star Badge / Surgeon of the Year (Times Network 2025)
  <svg key="badge" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
  </svg>,
  // 3. Ribbon / Medal (DMA Excellence in Medicine 2025)
  <svg key="ribbon" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>,
  // 4. Microphone / Faculty (ACAAD 2025)
  <svg key="mic" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
  </svg>,
  // 5. Globe / Summit (DAAS Summit 2025)
  <svg key="globe" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>,
  // 6. Star / Academic Honors (Mid Dermacon 2024 Ranchi)
  <svg key="star" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
  </svg>,
  // 7. Globe (World FUE 2023 Bangkok)
  <svg key="globe-2" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>,
  // 8. Microphone / Speaker (Visicon Conference 2023 New Delhi)
  <svg key="mic-2" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
  </svg>,
  // 9. Ribbon / Medal (ISHRS 2022 Panama)
  <svg key="ribbon-2" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>,
  // 10. Map Pin (LA Hair Restoration 2021 USA)
  <svg key="pin-2" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>,
  // 11. Star (Banha 2017 Egypt)
  <svg key="star-2" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
  </svg>,
];

type TabType = "all" | "recent" | "international";

export function Awards() {
  const [activeTab, setActiveTab] = useState<TabType>("all");
  const [page, setPage] = useState<number>(0); // 0: items 0..5, 1: items 6..10
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Split into categories
  const recentAwards = useMemo(() => AWARDS.slice(0, 6), []);
  const internationalAwards = useMemo(() => AWARDS.slice(6), []);

  // Determine current items shown based on tab and page
  const displayedAwards = useMemo(() => {
    if (activeTab === "recent") return recentAwards;
    if (activeTab === "international") return internationalAwards;
    return page === 0 ? recentAwards : internationalAwards;
  }, [activeTab, page, recentAwards, internationalAwards]);

  // Adjust hoveredIndex when tab or page changes without triggering cascading effect renders
  const [prevTabKey, setPrevTabKey] = useState(`${activeTab}:${page}`);
  if (prevTabKey !== `${activeTab}:${page}`) {
    setPrevTabKey(`${activeTab}:${page}`);
    setHoveredIndex(0);
  }

  // Auto-playing rotation across items and pages
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setHoveredIndex((prev) => {
        const currentLen = displayedAwards.length;
        if (prev === null) return 0;
        const next = prev + 1;
        if (next < currentLen) {
          return next;
        }
        // If we reached the end of the current page in 'all' mode, flip page
        if (activeTab === "all") {
          setPage((p) => (p === 0 ? 1 : 0));
        }
        return 0;
      });
    }, 2800);

    return () => clearInterval(interval);
  }, [isAutoPlaying, displayedAwards.length, activeTab]);

  const handleNextPage = () => {
    if (activeTab === "recent") {
      setActiveTab("international");
    } else if (activeTab === "international") {
      setActiveTab("recent");
    } else {
      setPage((p) => (p === 0 ? 1 : 0));
    }
  };

  const handlePrevPage = () => {
    if (activeTab === "recent") {
      setActiveTab("international");
    } else if (activeTab === "international") {
      setActiveTab("recent");
    } else {
      setPage((p) => (p === 0 ? 1 : 0));
    }
  };

  return (
    <section id="awards" className="py-24 bg-brand-black text-white relative overflow-hidden">
      {/* Subtle ambient texture */}
      <div className="absolute inset-0 brand-cell-pattern opacity-[0.05]" />
      
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="mx-auto mb-10 max-w-2xl text-center">
          <span className="font-accent text-xs uppercase text-brand-peach">
            Awards & Achievements
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight text-white">
            Recognised on the <span className="text-brand-orange">World Stage</span>
          </h2>
          <p className="mt-4 text-sm text-white/60 leading-relaxed">
            Dr. Gaurav Garg regularly speaks and receives top honors at leading national and international medical summits.
          </p>
        </Reveal>

        {/* Tab Filters and Navigation Controls */}
        <div className="mb-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Category Tabs */}
          <div className="inline-flex items-center gap-1.5 p-1 rounded-full bg-white/5 border border-white/10 text-xs sm:text-sm font-medium">
            <button
              onClick={() => {
                setActiveTab("all");
                setPage(0);
              }}
              className={`px-4 py-1.5 rounded-full transition-all duration-300 ${
                activeTab === "all"
                  ? "bg-brand-orange text-white shadow-md shadow-brand-orange/30 font-semibold"
                  : "text-white/60 hover:text-white hover:bg-white/5"
              }`}
            >
              All Achievements (11)
            </button>
            <button
              onClick={() => {
                setActiveTab("recent");
                setPage(0);
              }}
              className={`px-4 py-1.5 rounded-full transition-all duration-300 ${
                activeTab === "recent"
                  ? "bg-brand-orange text-white shadow-md shadow-brand-orange/30 font-semibold"
                  : "text-white/60 hover:text-white hover:bg-white/5"
              }`}
            >
              Latest Honors (2024–2026)
            </button>
            <button
              onClick={() => {
                setActiveTab("international");
                setPage(1);
              }}
              className={`px-4 py-1.5 rounded-full transition-all duration-300 ${
                activeTab === "international"
                  ? "bg-brand-orange text-white shadow-md shadow-brand-orange/30 font-semibold"
                  : "text-white/60 hover:text-white hover:bg-white/5"
              }`}
            >
              Global Stage (2017–2023)
            </button>
          </div>

          {/* Prev/Next arrows & Page counter */}
          <div className="flex items-center gap-3">
            {activeTab === "all" && (
              <span className="text-xs text-white/50 font-medium">
                Showing {page === 0 ? "1–6 of 11 (Latest)" : "7–11 of 11 (Global)"}
              </span>
            )}
            <div className="flex items-center gap-1.5">
              <button
                onClick={handlePrevPage}
                aria-label="Previous awards"
                className="h-9 w-9 rounded-full border border-white/15 bg-white/5 hover:bg-brand-orange hover:border-brand-orange text-white flex items-center justify-center transition-all duration-300 group"
              >
                <svg className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={handleNextPage}
                aria-label="Next awards"
                className="h-9 w-9 rounded-full border border-white/15 bg-white/5 hover:bg-brand-orange hover:border-brand-orange text-white flex items-center justify-center transition-all duration-300 group"
              >
                <svg className="w-4 h-4 transition-transform group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Space-saving Expanding Horizontal Cards */}
        <AnimatePresence mode="wait">
          <motion.div 
            key={activeTab + String(page)}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35 }}
            className="flex flex-col lg:flex-row h-auto lg:h-[350px] gap-3.5 w-full"
            onMouseEnter={() => setIsAutoPlaying(false)}
            onMouseLeave={() => setIsAutoPlaying(true)}
          >
            {displayedAwards.map((award, i) => {
              const isHovered = hoveredIndex === i;
              // Map icon based on global award index
              const globalIndex = AWARDS.findIndex(
                (a) => a.title === award.title && a.year === award.year
              );
              const icon = AWARD_ICONS[globalIndex >= 0 ? globalIndex % AWARD_ICONS.length : i % AWARD_ICONS.length];
              const image = award.image || "/images/awards/new_delhi.webp";

              return (
                <motion.div
                  key={award.title + award.year}
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  onClick={() => setHoveredIndex(i)}
                  className="relative cursor-pointer rounded-3xl overflow-hidden border border-white/10 bg-white/5 group"
                  animate={{
                    flex: hoveredIndex === null ? 1 : isHovered ? 3.5 : 1,
                  }}
                  transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  style={{
                    minHeight: "110px", // fallback for mobile stacking
                  }}
                >
                  {/* Background ambient glow when active */}
                  <AnimatePresence>
                    {isHovered && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5 }}
                        className="absolute inset-0 z-0"
                      >
                        <Image 
                          src={image}
                          alt={`${award.place} Skyline`}
                          fill
                          className="object-cover opacity-40 mix-blend-overlay transition-transform duration-[10s] group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-brand-black via-brand-black/50 to-brand-orange/20" />
                        <div className="absolute inset-0 bg-gradient-to-r from-brand-black/80 via-transparent to-brand-peach/10" />
                        
                        {/* Large year watermark */}
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[12rem] font-heading font-bold text-white/[0.02] pointer-events-none select-none whitespace-nowrap">
                          {award.year}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="relative z-10 flex flex-col h-full justify-end p-6 sm:p-7">
                    {/* Top-left Icon */}
                    <div className="absolute top-6 left-6 sm:top-7 sm:left-7">
                       <div className={`h-11 w-11 rounded-full flex items-center justify-center transition-all duration-500 ${isHovered ? 'bg-brand-orange text-white shadow-lg shadow-brand-orange/30 scale-110' : 'bg-white/5 text-brand-orange scale-100 group-hover:bg-white/10'}`}>
                          {icon}
                       </div>
                    </div>

                    {/* Text Details Container */}
                    <motion.div layout className="flex flex-col mt-16 lg:mt-auto">
                      <motion.p 
                        layout="position"
                        className={`font-accent text-3xl sm:text-4xl transition-colors duration-500 ${isHovered ? 'text-brand-peach' : 'text-brand-orange'}`}
                      >
                        {award.year}
                      </motion.p>
                      
                      <motion.div
                        className="overflow-hidden"
                        animate={{ opacity: isHovered ? 1 : 0.5 }}
                        transition={{ duration: 0.4 }}
                      >
                        <h3 className={`mt-2 font-semibold transition-all duration-500 ${isHovered ? 'text-xl sm:text-2xl text-white drop-shadow-md' : 'text-base sm:text-lg text-white/80'} line-clamp-2`}>
                          {award.title}
                        </h3>
                        
                        {/* Subtitle & location reveal only when hovered */}
                        <AnimatePresence>
                          {isHovered && (
                            <motion.div
                              initial={{ opacity: 0, height: 0, marginTop: 0 }}
                              animate={{ opacity: 1, height: "auto", marginTop: 8 }}
                              exit={{ opacity: 0, height: 0, marginTop: 0 }}
                              transition={{ duration: 0.3 }}
                              className="flex flex-col gap-1.5 drop-shadow-sm"
                            >
                              {award.subtitle && (
                                <p className="text-white/80 text-xs sm:text-sm font-medium line-clamp-1">
                                  {award.subtitle}
                                </p>
                              )}
                              <div className="flex items-center gap-2 text-brand-peach/95 text-xs sm:text-sm font-semibold">
                                <span className="h-1.5 w-1.5 rounded-full bg-brand-orange inline-block shadow-[0_0_8px_rgba(229,110,62,0.9)]" />
                                {award.place}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    </motion.div>
                  </div>
                </motion.div>
              );
            })}

          </motion.div>
        </AnimatePresence>

        {/* Bottom Pagination Dots in 'All' mode */}
        {activeTab === "all" && (
          <div className="mt-8 flex justify-center items-center gap-2">
            <button
              onClick={() => setPage(0)}
              aria-label="View page 1"
              className={`h-2 rounded-full transition-all duration-300 ${
                page === 0 ? "w-8 bg-brand-orange" : "w-2 bg-white/20 hover:bg-white/40"
              }`}
            />
            <button
              onClick={() => setPage(1)}
              aria-label="View page 2"
              className={`h-2 rounded-full transition-all duration-300 ${
                page === 1 ? "w-8 bg-brand-orange" : "w-2 bg-white/20 hover:bg-white/40"
              }`}
            />
          </div>
        )}
      </div>
    </section>
  );
}
